/**
 * TK-39 §3 (lee Q2) — 32 TIMER DIM của SLCU và luật SEEK. Tệp thuần (không nhập gì của trình duyệt) để bộ kiểm chạy thẳng.
 *
 * Luật seek (giống hệt bộ giả lập 1.4 `TheNema.seek`): với nhóm N, xét mọi timer `nhom = N` (255 = không dùng, không bao giờ xét),
 * mỗi timer lùi tối đa 7 ngày tìm lần chạy GẦN NHẤT đã qua (≤ bây giờ) rơi vào ngày bật; lần chạy mới nhất trong các timer thắng;
 * cùng phút ⇒ timer `so` LỚN hơn thắng. Không timer nào ⇒ null (đèn giữ mức đang có).
 * Giờ là giờ VN; `thu` bitmask 7 bit: bit0 = CN, bit1 = T2 … bit6 = T7.
 */
export const SO_TIMER = 32;
export const NHOM_KHONG_DUNG = 255;
export const NGAY_LUI = 7;

export type TimerDim = { so: number; gio_phut: number; muc: number; thu: number; nhom: number };
/** Dạng trên dây (`NemaLenh.thamSo.timers` của lệnh `lich_dim`) — hợp đồng với SLCU, không đổi tên trường. */
export type TimerDay = { so: number; gio: string; muc: number; thu: boolean[]; nhom: number };
export type LucVN = { thu: number; phut: number };   // thu 0 = CN … 6 = T7; phut 0..1439
export type KetSeek = { muc: number; so: number; phutTruoc: number };

const MUI_VN_MS = 7 * 3600_000;
export const lucVN = (d: Date): LucVN => {
  const v = new Date(d.getTime() + MUI_VN_MS);
  return { thu: v.getUTCDay(), phut: v.getUTCHours() * 60 + v.getUTCMinutes() };
};

export const coThu = (thu: number, i: number) => ((thu >> i) & 1) === 1;

export function seekDim(timers: TimerDim[], nhom: number, luc: LucVN): KetSeek | null {
  if (nhom === NHOM_KHONG_DUNG) return null;
  let tot: KetSeek | null = null;
  for (const t of timers) {
    if (t.nhom === NHOM_KHONG_DUNG || t.nhom !== nhom) continue;
    for (let lui = 0; lui <= NGAY_LUI; lui++) {
      const truoc = lui * 1440 + luc.phut - t.gio_phut;
      if (truoc < 0) continue;
      if (!coThu(t.thu, (luc.thu - lui + 7 * 2) % 7)) continue;
      if (!tot || truoc < tot.phutTruoc || (truoc === tot.phutTruoc && t.so > tot.so)) tot = { muc: t.muc, so: t.so, phutTruoc: truoc };
      break;
    }
  }
  return tot;
}

/** Mức của nhóm theo từng `buoc` phút trong một ngày `thu` (đồ thị). null = chưa có timer nào chạy ⇒ đèn giữ mức cũ. */
export function duongMuc(timers: TimerDim[], nhom: number, thu: number, buoc = 5): (number | null)[] {
  const ra: (number | null)[] = [];
  for (let p = 0; p < 1440; p += buoc) ra.push(seekDim(timers, nhom, { thu, phut: p })?.muc ?? null);
  return ra;
}

const hai = (n: number) => String(n).padStart(2, '0');
export const gioChu = (p: number) => `${hai(Math.floor(p / 60))}:${hai(p % 60)}`;
export const raDay = (t: TimerDim): TimerDay => ({
  so: t.so, gio: gioChu(t.gio_phut), muc: t.muc, thu: Array.from({ length: 7 }, (_, i) => coThu(t.thu, i)), nhom: t.nhom,
});
export const tuDay = (t: TimerDay): TimerDim => ({
  so: t.so, gio_phut: Number(t.gio.slice(0, 2)) * 60 + Number(t.gio.slice(3, 5)), muc: t.muc,
  thu: t.thu.reduce((m, b, i) => (b ? m | (1 << i) : m), 0), nhom: t.nhom,
});
/** Đủ 32 timer theo số 1..32: thiếu số nào ⇒ timer trống (nhóm 255, mọi ngày, 00:00, 0 %). */
export const du32 = (ds: TimerDim[]): TimerDim[] =>
  Array.from({ length: SO_TIMER }, (_, i) => ds.find((t) => t.so === i + 1) ?? { so: i + 1, gio_phut: 0, muc: 0, thu: 0x7f, nhom: NHOM_KHONG_DUNG });
